/**
 * GridView.jsx
 * ────────────
 * Spreadsheet-style grid for the Lifecycle Recon Hub.
 * Phases 1–3: grid render, inline editing, sort, dirty state.
 * Phase 4: fill-down, copy/paste, selection model.
 *
 * Interaction model:
 *   Single click  → SELECT cell (highlight, no editor)
 *   Shift+click   → extend selection (column range or row range)
 *   Double click   → EDIT cell (open inline editor)
 *   Ctrl+D        → fill down (column range selection)
 *   Ctrl+C        → copy selected cells to clipboard
 *   Ctrl+V        → paste from clipboard into selection
 *   Escape        → clear selection / cancel edit
 *   Delete        → clear selected editable cell values
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { LIFECYCLE_COLUMNS } from './lifecycleColumnManifest';
import { useReferenceData } from '../../shared/hooks/useReferenceData';
import { FixedListSelect } from '../../shared/components/FixedListSelect';
import ColumnVisibilityPanel from './ColumnVisibilityPanel';
import SavedViewsPanel from './SavedViewsPanel';
import SortPanel from './SortPanel';
import './GridView.css';

/* ── ReferenceData keys used by combobox columns (lifecycle default) ──── */
function getRefDataKeys(cols) {
  return cols
    .filter(c => c.type === 'combobox' && c.refKey)
    .map(c => c.refKey)
    .filter((v, i, a) => a.indexOf(v) === i);
}

/* ── Column manifest lookup ───────────────────────────────────────────── */
function buildColByKey(cols) {
  const map = {};
  for (const col of cols) map[col.key] = col;
  return map;
}

/* ── Format helpers ───────────────────────────────────────────────────── */
function fmtDate(v) {
  if (!v) return '';
  // Return raw YYYY-MM-DD string — no Date object conversion to avoid timezone offset
  return String(v);
}

function fmtNumber(v) {
  if (v == null || v === '') return '';
  return Number(v).toLocaleString();
}

/* ── Strict combobox columns (manifest `strict`) ───────────────────────
   Bulk writes (paste, Fill Down, Delete) into a strict column take only
   values on its refdata list. A string is NORMALIZED first — trimmed and
   uppercased — and the normalized value is what gets written, so a pasted
   'gm' lands as GM. Blank is never on a list, so a strict column can be
   neither cleared nor sent an off-list value by a bulk write.

   ⚠️ DELIBERATELY DIFFERENT FROM FixedListSelect's isOffList, which is
   case-sensitive with no normalization. That one decides how a STORED value
   DISPLAYS: a stored 'Toyota' must show read-only as off-list, not masquerade
   as TOYOTA. This one decides what a bulk WRITE may put there. Different
   operations, different rules — do not "fix" either to match the other. */
function isStrictColumn(col) {
  return !!col && col.type === 'combobox' && !!col.strict;
}

/** The value a bulk write sends: normalized on a strict column, otherwise the
 *  input itself, untouched. */
export function normalizeStrict(col, value) {
  if (!isStrictColumn(col) || typeof value !== 'string') return value;
  return value.trim().toUpperCase();
}

/** True when a bulk write must SKIP this value (strict column, not on list). */
export function strictRejects(col, value, refOpts) {
  if (!isStrictColumn(col)) return false;
  return !((refOpts && refOpts[col.refKey]) || []).includes(normalizeStrict(col, value));
}

/** Delete/Backspace over a set of `${rowId}:${colKey}` cells: the blank each
 *  editable cell takes, with strict columns refusing it (left unchanged,
 *  counted). Pure; handleDeleteSelected applies `writes` in order. Today's UI
 *  only ever selects one column, but computeSelectedCells also builds
 *  single-row multi-column ranges, so mixed columns are handled here. */
export function planDelete(cellKeys, colByKey, refOpts) {
  const writes = [];
  const labels = new Set();
  let skipped = 0;
  for (const cellKey of cellKeys) {
    const [rowId, colKey] = cellKey.split(':');
    const col = colByKey[colKey];
    if (!col || col.readOnly) continue;
    const emptyVal = col.type === 'boolean' ? false : '';
    if (strictRejects(col, emptyVal, refOpts)) {
      skipped += 1;
      labels.add(col.label);
      continue;
    }
    writes.push([rowId, colKey, emptyVal]);
  }
  return { writes, skipped, labels: [...labels] };
}

function renderCellValue(record, col) {
  const raw = record[col.key];
  if (raw == null || raw === '') return '';
  switch (col.type) {
    case 'boolean': return raw ? '\u2713' : '';
    case 'date':    return fmtDate(raw);
    case 'number':  return fmtNumber(raw);
    default:        return String(raw);
  }
}

function getRawValue(record, col, pendingChanges) {
  const rowId = record.id || record.tool_id;
  const rowChanges = pendingChanges.get(rowId);
  if (rowChanges && col.key in rowChanges) return rowChanges[col.key];
  const raw = record[col.key];
  if (raw == null) return '';
  return raw;
}

/* ── Effective value: pending override or original ────────────────────── */
function getEffectiveValue(record, colKey, pendingChanges) {
  const rowId = record.id || record.tool_id;
  const rowChanges = pendingChanges.get(rowId);
  if (rowChanges && colKey in rowChanges) return rowChanges[colKey];
  return record[colKey] ?? '';
}

/* ═══════════════════════════════════════════════════════════════════════
   Selection helpers
   ═══════════════════════════════════════════════════════════════════════ */

/**
 * Compute set of "rowId:colKey" strings from anchor + focus.
 * Phase 1 constraint: either single-column multi-row (fill-down)
 * or single-row multi-column (copy/paste). No full rectangles yet.
 */
function computeSelectedCells(anchor, focus, displayRecords, visibleColumns) {
  if (!anchor) return new Set();
  const f = focus || anchor;

  const rowIds = displayRecords.map(r => r.id || r.tool_id);
  const colKeys = visibleColumns.map(c => c.key);

  const aRow = rowIds.indexOf(anchor.rowId);
  const fRow = rowIds.indexOf(f.rowId);
  const aCol = colKeys.indexOf(anchor.colKey);
  const fCol = colKeys.indexOf(f.colKey);

  if (aRow === -1 || fRow === -1 || aCol === -1 || fCol === -1) return new Set();

  const cells = new Set();

  if (aCol === fCol) {
    // Column range (fill-down pattern)
    const minR = Math.min(aRow, fRow);
    const maxR = Math.max(aRow, fRow);
    for (let r = minR; r <= maxR; r++) cells.add(`${rowIds[r]}:${colKeys[aCol]}`);
  } else {
    // Single-row multi-column (Phase 1 cap: use anchor row)
    const minC = Math.min(aCol, fCol);
    const maxC = Math.max(aCol, fCol);
    for (let c = minC; c <= maxC; c++) cells.add(`${rowIds[aRow]}:${colKeys[c]}`);
  }

  return cells;
}

function isColumnRange(anchor, focus) {
  if (!anchor || !focus) return false;
  return anchor.colKey === focus.colKey && anchor.rowId !== focus.rowId;
}

/* ── Default column widths by type (px) ───────────────────────────────── */
function getColumnWidth(col) {
  if (col.width) return col.width;
  switch (col.type) {
    case 'boolean':  return 80;
    case 'number':   return 100;
    case 'date':     return 120;
    case 'combobox': return 140;
    default:         return 150; // text
  }
}

/* ═══════════════════════════════════════════════════════════════════════
   Cell Editors
   ═══════════════════════════════════════════════════════════════════════ */

function TextCellEditor({ value, onCommit, onCancel, onNav }) {
  const ref = useRef(null);
  const committedRef = useRef(false);
  const mountedRef = useRef(false);
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
    requestAnimationFrame(() => { mountedRef.current = true; });
  }, []);
  function commit() {
    if (committedRef.current) return;
    committedRef.current = true;
    onCommit(local);
  }
  function handleBlur() {
    if (!mountedRef.current) return;
    commit();
  }
  function handleKeyDown(e) {
    if (e.key === 'Enter')     { e.preventDefault(); e.stopPropagation(); commit(); onNav?.('down'); return; }
    if (e.key === 'Tab')       { e.preventDefault(); e.stopPropagation(); commit(); onNav?.(e.shiftKey ? 'prev' : 'next'); return; }
    if (e.key === 'Escape')    { e.preventDefault(); e.stopPropagation(); onCancel(); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); e.stopPropagation(); commit(); onNav?.('up'); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); e.stopPropagation(); commit(); onNav?.('down'); return; }
    if (e.key === 'ArrowLeft' && ref.current?.selectionStart === 0 && ref.current?.selectionEnd === 0) {
      e.preventDefault(); e.stopPropagation(); commit(); onNav?.('left'); return;
    }
    if (e.key === 'ArrowRight' && ref.current?.selectionStart === local.length && ref.current?.selectionEnd === local.length) {
      e.preventDefault(); e.stopPropagation(); commit(); onNav?.('right'); return;
    }
  }
  return (
    <input ref={ref} type="text" className="gv-cell-input" value={local}
      onChange={e => setLocal(e.target.value)} onBlur={handleBlur} onKeyDown={handleKeyDown} />
  );
}

function NumberCellEditor({ value, onCommit, onCancel, onNav }) {
  const ref = useRef(null);
  const committedRef = useRef(false);
  const mountedRef = useRef(false);
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => {
    ref.current?.focus(); ref.current?.select();
    requestAnimationFrame(() => { mountedRef.current = true; });
  }, []);
  function commit() {
    if (committedRef.current) return;
    committedRef.current = true;
    onCommit(local === '' ? '' : Number(local));
  }
  function handleBlur() {
    if (!mountedRef.current) return;
    commit();
  }
  function handleKeyDown(e) {
    if (e.key === 'Enter')      { e.preventDefault(); e.stopPropagation(); commit(); onNav?.('down'); return; }
    if (e.key === 'Tab')        { e.preventDefault(); e.stopPropagation(); commit(); onNav?.(e.shiftKey ? 'prev' : 'next'); return; }
    if (e.key === 'Escape')     { e.preventDefault(); e.stopPropagation(); onCancel(); return; }
    if (e.key === 'ArrowUp')    { e.preventDefault(); e.stopPropagation(); commit(); onNav?.('up'); return; }
    if (e.key === 'ArrowDown')  { e.preventDefault(); e.stopPropagation(); commit(); onNav?.('down'); return; }
    if (e.key === 'ArrowLeft')  { e.preventDefault(); e.stopPropagation(); commit(); onNav?.('left'); return; }
    if (e.key === 'ArrowRight') { e.preventDefault(); e.stopPropagation(); commit(); onNav?.('right'); return; }
  }
  return (
    <input ref={ref} type="number" className="gv-cell-input" value={local}
      onChange={e => setLocal(e.target.value)} onBlur={handleBlur} onKeyDown={handleKeyDown} />
  );
}

function DateCellEditor({ value, onCommit, onCancel, onNav }) {
  const ref = useRef(null);
  const committedRef = useRef(false);
  const mountedRef = useRef(false);
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => {
    ref.current?.focus();
    requestAnimationFrame(() => { mountedRef.current = true; });
  }, []);
  function commit() {
    if (committedRef.current) return;
    committedRef.current = true;
    onCommit(local);
  }
  function handleBlur() {
    if (!mountedRef.current) return;
    commit();
  }
  function handleKeyDown(e) {
    if (e.key === 'Enter')      { e.preventDefault(); e.stopPropagation(); commit(); onNav?.('down'); return; }
    if (e.key === 'Tab')        { e.preventDefault(); e.stopPropagation(); commit(); onNav?.(e.shiftKey ? 'prev' : 'next'); return; }
    if (e.key === 'Escape')     { e.preventDefault(); e.stopPropagation(); onCancel(); return; }
    if (e.key === 'ArrowUp')    { e.preventDefault(); e.stopPropagation(); commit(); onNav?.('up'); return; }
    if (e.key === 'ArrowDown')  { e.preventDefault(); e.stopPropagation(); commit(); onNav?.('down'); return; }
    if (e.key === 'ArrowLeft')  { e.preventDefault(); e.stopPropagation(); commit(); onNav?.('left'); return; }
    if (e.key === 'ArrowRight') { e.preventDefault(); e.stopPropagation(); commit(); onNav?.('right'); return; }
  }
  return (
    <input ref={ref} type="date" className="gv-cell-input" value={local}
      onChange={e => setLocal(e.target.value)} onBlur={handleBlur} onKeyDown={handleKeyDown} />
  );
}

function BooleanCellEditor({ value, onCommit, onNav }) {
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  function handleKeyDown(e) {
    if (e.key === 'Tab')        { e.preventDefault(); e.stopPropagation(); onNav?.(e.shiftKey ? 'prev' : 'next'); return; }
    if (e.key === 'Escape')     { e.preventDefault(); e.stopPropagation(); onNav?.('escape'); return; }
    if (e.key === 'ArrowUp')    { e.preventDefault(); e.stopPropagation(); onNav?.('up'); return; }
    if (e.key === 'ArrowDown')  { e.preventDefault(); e.stopPropagation(); onNav?.('down'); return; }
    if (e.key === 'ArrowLeft')  { e.preventDefault(); e.stopPropagation(); onNav?.('left'); return; }
    if (e.key === 'ArrowRight') { e.preventDefault(); e.stopPropagation(); onNav?.('right'); return; }
    if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); e.stopPropagation(); onCommit(false); return; }
  }
  return (
    <input ref={ref} type="checkbox" className="gv-cell-checkbox" checked={!!value}
      onChange={e => onCommit(e.target.checked)} onKeyDown={handleKeyDown} />
  );
}

function ComboBoxCellEditor({ value, options, strict = false, onCommit, onCancel, onNav }) {
  const ref = useRef(null);
  const committedRef = useRef(false);
  const mountedRef = useRef(false);
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => {
    ref.current?.focus();
    requestAnimationFrame(() => { mountedRef.current = true; });
  }, []);
  function commit(val) {
    if (committedRef.current) return;
    committedRef.current = true;
    onCommit(val);
  }
  function handleKeyDown(e) {
    if (e.key === 'Escape')     { e.preventDefault(); e.stopPropagation(); onCancel(); return; }
    if (e.key === 'Tab')        { e.preventDefault(); e.stopPropagation(); commit(local); onNav?.(e.shiftKey ? 'prev' : 'next'); return; }
    if (e.key === 'ArrowUp')    { e.preventDefault(); e.stopPropagation(); commit(local); onNav?.('up'); return; }
    if (e.key === 'ArrowDown')  { e.preventDefault(); e.stopPropagation(); commit(local); onNav?.('down'); return; }
    if (e.key === 'ArrowLeft')  { e.preventDefault(); e.stopPropagation(); commit(local); onNav?.('left'); return; }
    if (e.key === 'ArrowRight') { e.preventDefault(); e.stopPropagation(); commit(local); onNav?.('right'); return; }
  }
  // strict column (manifest flag): an off-list stored value shows read-only
  // beside the control instead of falling back to '—', and blank is never a
  // choice. Every other combobox column takes the unchanged path below.
  if (strict) {
    return (
      <FixedListSelect selectRef={ref} className="gv-cell-select" layout="inline"
        value={local} options={options} placeholder="—"
        onChange={v => { setLocal(v); committedRef.current = false; commit(v); }}
        onBlur={() => { if (mountedRef.current) commit(local); }} onKeyDown={handleKeyDown} />
    );
  }
  return (
    <select ref={ref} className="gv-cell-select" value={local}
      onChange={e => { setLocal(e.target.value); committedRef.current = false; commit(e.target.value); }}
      onBlur={() => { if (mountedRef.current) commit(local); }} onKeyDown={handleKeyDown}>
      <option value="">—</option>
      {(options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
    </select>
  );
}

/* ── Sort indicator arrow ─────────────────────────────────────────────── */
const MAX_SORT_LEVELS = 5;

function SortArrow({ direction, priority }) {
  const arrow = direction === 'asc' ? '\u2191' : '\u2193';
  const opacity = priority === 0 ? 1 : 0.45;
  return <span className="gv-sort-arrow" style={{ opacity }}>{arrow}</span>;
}

/* ═══════════════════════════════════════════════════════════════════════
   GridView Main Component
   ═══════════════════════════════════════════════════════════════════════ */

export default function GridView({
  records,
  sortedRecords,
  sortKeys,
  setSortKeys,
  columns = LIFECYCLE_COLUMNS,
  screen = 'lifecycle',
  callerEmail,
  siteId,
  onSave,
  onCancel,
  columnVisibility,
  toggleColumn,
  showAllColumns,
  hideAllColumns,
  pendingChanges,
  setPendingChange,
  clearPendingChanges,
  isDirty,
  saving,
  saveError,
  savedViews,
  activeViewId,
  activeViewName,
  saveView,
  deleteView,
  loadView,
}) {
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [colPanelOpen, setColPanelOpen] = useState(false);
  const [viewsPanelOpen, setViewsPanelOpen] = useState(false);
  const [sortPanelOpen, setSortPanelOpen] = useState(false);

  /* ── Highlight state: which cell has the visual highlight ────────────── */
  const [activeCell, setActiveCell] = useState(null);
  /* ── Editor state: which cell has an open editor (separate lifecycle) ── */
  const [editingCell, setEditingCell] = useState(null);

  /* ── Strict-column skip notice: { count, labels } | null ─────────────── */
  // Set by paste / Fill Down / Delete when a strict column refused values;
  // cleared by the next of those that refuses nothing, or by dismiss.
  const [strictNotice, setStrictNotice] = useState(null);

  /* ── Select mode: anchor + focus for range selection ───────────────── */
  // { anchor: { rowId, colKey }, focus: { rowId, colKey } } | null
  const [selection, setSelection] = useState(null);

  /* ── Derived lookups from columns prop ───────────────────────────────── */
  const refDataKeys = useMemo(() => getRefDataKeys(columns), [columns]);
  const colByKey = useMemo(() => buildColByKey(columns), [columns]);

  /* ── Load ReferenceData for all combobox columns ───────────────────── */
  const { options: refOpts } = useReferenceData(refDataKeys, callerEmail, siteId);

  /* ── Visible columns ────────────────────────────────────────────────── */
  const visibleColumns = useMemo(() => {
    return columns.filter(col => columnVisibility[col.key] !== false);
  }, [columnVisibility, columns]);

  const displayRecords = sortedRecords || records;

  /* ── Selected cells set ─────────────────────────────────────────────── */
  const selectedCells = useMemo(() => {
    if (!selection) return new Set();
    return computeSelectedCells(selection.anchor, selection.focus, displayRecords, visibleColumns);
  }, [selection, displayRecords, visibleColumns]);

  const hasColumnRange = selection && isColumnRange(selection.anchor, selection.focus);

  /* ── Sort key lookup ────────────────────────────────────────────────── */
  const sortKeyMap = useMemo(() => {
    const map = {};
    (sortKeys || []).forEach((sk, i) => { map[sk.field] = { direction: sk.direction, priority: i }; });
    return map;
  }, [sortKeys]);

  /* ── Column header click → sort ─────────────────────────────────────── */
  const handleHeaderClick = useCallback((colKey) => {
    const current = sortKeys || [];
    const existing = current.findIndex(sk => sk.field === colKey);
    if (existing === -1) {
      // Not sorted: add as primary asc, push others down
      let next = [{ field: colKey, direction: 'asc' }, ...current];
      if (next.length > MAX_SORT_LEVELS) next = next.slice(0, MAX_SORT_LEVELS);
      setSortKeys(next);
    } else {
      // Already sorted: flip direction (never remove)
      setSortKeys(current.map((sk, i) =>
        i === existing ? { ...sk, direction: sk.direction === 'asc' ? 'desc' : 'asc' } : sk
      ));
    }
  }, [sortKeys, setSortKeys]);

  /* ── Scroll-into-view helper ─────────────────────────────────────────── */
  // Manual scroll on the grid's own .gv-table-wrap container to avoid
  // viewport-based miscalculations caused by the fixed AppShell sidebar.
  const scrollToCell = useCallback((rowId, colKey) => {
    requestAnimationFrame(() => {
      const container = tableRef.current;
      const td = container?.querySelector(`[data-cell="${rowId}:${colKey}"]`);
      if (!td || !container) return;

      const cRect = container.getBoundingClientRect();
      const tRect = td.getBoundingClientRect();

      // Horizontal scroll — account for sticky first column (150px)
      const stickyOffset = 160;
      if (tRect.left < cRect.left + stickyOffset) {
        container.scrollLeft -= (cRect.left + stickyOffset - tRect.left);
      } else if (tRect.right > cRect.right) {
        container.scrollLeft += (tRect.right - cRect.right);
      }

      // Vertical scroll — account for sticky header (32px)
      const headerOffset = 32;
      if (tRect.top < cRect.top + headerOffset) {
        container.scrollTop -= (cRect.top + headerOffset - tRect.top);
      } else if (tRect.bottom > cRect.bottom) {
        container.scrollTop += (tRect.bottom - cRect.bottom);
      }
    });
  }, []);

  /* ── Navigation helpers (for edit mode Tab/Enter) ───────────────────── */
  const getEditableColIndices = useCallback(() => {
    return visibleColumns.map((col, i) => ({ col, i })).filter(({ col }) => !col.readOnly);
  }, [visibleColumns]);

  const navigate = useCallback((direction, currentRowId, currentColKey) => {
    const editableCols = getEditableColIndices();
    if (editableCols.length === 0) return;
    const currentColIdx = editableCols.findIndex(({ col }) => col.key === currentColKey);
    const rowIds = displayRecords.map(r => r.id || r.tool_id);
    const currentRowIdx = rowIds.indexOf(currentRowId);
    let nextRowId, nextColKey;
    if (direction === 'next') {
      if (currentColIdx < editableCols.length - 1) {
        nextRowId = currentRowId; nextColKey = editableCols[currentColIdx + 1].col.key;
      } else if (currentRowIdx < displayRecords.length - 1) {
        nextRowId = rowIds[currentRowIdx + 1]; nextColKey = editableCols[0].col.key;
      } else {
        nextRowId = rowIds[0]; nextColKey = editableCols[0].col.key;
      }
    } else if (direction === 'prev') {
      if (currentColIdx > 0) {
        nextRowId = currentRowId; nextColKey = editableCols[currentColIdx - 1].col.key;
      } else if (currentRowIdx > 0) {
        nextRowId = rowIds[currentRowIdx - 1]; nextColKey = editableCols[editableCols.length - 1].col.key;
      } else {
        nextRowId = rowIds[rowIds.length - 1]; nextColKey = editableCols[editableCols.length - 1].col.key;
      }
    } else if (direction === 'down') {
      if (currentRowIdx < displayRecords.length - 1) {
        nextRowId = rowIds[currentRowIdx + 1]; nextColKey = currentColKey;
      } else {
        nextRowId = rowIds[0]; nextColKey = currentColKey;
      }
    }
    if (nextRowId && nextColKey) {
      setActiveCell({ rowId: nextRowId, colKey: nextColKey });
      setEditingCell({ rowId: nextRowId, colKey: nextColKey });
      scrollToCell(nextRowId, nextColKey);
    }
  }, [displayRecords, getEditableColIndices, scrollToCell]);

  /* ── Cell commit logic ──────────────────────────────────────────────── */
  const commitCellValue = useCallback((record, col, newValue) => {
    const rowId = record.id || record.tool_id;
    const originalValue = record[col.key];
    const origNorm = (originalValue == null || originalValue === '') ? '' : originalValue;
    const newNorm = (newValue == null || newValue === '') ? '' : newValue;
    let isEqual;
    if (col.type === 'number') {
      isEqual = (origNorm === '' && newNorm === '') || Number(origNorm) === Number(newNorm);
    } else if (col.type === 'boolean') {
      isEqual = !!origNorm === !!newNorm;
    } else {
      isEqual = String(origNorm) === String(newNorm);
    }
    if (isEqual) {
      setPendingChange(rowId, col.key, undefined);
    } else {
      setPendingChange(rowId, col.key, newValue);
    }
  }, [setPendingChange]);

  /* ── Bulk commit: set a value on a specific rowId+colKey ────────────── */
  const commitValueByIds = useCallback((rowId, colKey, newValue) => {
    const record = displayRecords.find(r => (r.id || r.tool_id) === rowId);
    const col = colByKey[colKey];
    if (!record || !col || col.readOnly) return;
    commitCellValue(record, col, newValue);
  }, [displayRecords, commitCellValue, colByKey]);

  /* ── Fill Down ──────────────────────────────────────────────────────── */
  const handleFillDown = useCallback(() => {
    if (!selection || !hasColumnRange) return;
    const colKey = selection.anchor.colKey;
    const col = colByKey[colKey];
    if (!col || col.readOnly) return;

    const rowIds = displayRecords.map(r => r.id || r.tool_id);
    const anchorIdx = rowIds.indexOf(selection.anchor.rowId);
    const focusIdx = rowIds.indexOf(selection.focus.rowId);
    const topIdx = Math.min(anchorIdx, focusIdx);

    // Source value = effective value of the top cell
    const topRecord = displayRecords[topIdx];
    const sourceValue = getEffectiveValue(topRecord, colKey, pendingChanges);

    // Apply to all selected cells. A strict column writes the normalized
    // source, and refuses one not on its list: those cells are left
    // unchanged and counted. Other columns write the source as-is.
    const fillValue = normalizeStrict(col, sourceValue);
    let skipped = 0;
    for (const cellKey of selectedCells) {
      const [rowId] = cellKey.split(':');
      if (rowId === rowIds[topIdx]) continue; // skip source cell
      if (strictRejects(col, fillValue, refOpts)) { skipped += 1; continue; }
      commitValueByIds(rowId, colKey, fillValue);
    }
    setStrictNotice(skipped > 0 ? { count: skipped, labels: [col.label] } : null);
  }, [selection, hasColumnRange, displayRecords, pendingChanges, selectedCells, commitValueByIds, colByKey, refOpts]);

  /* ── Copy to clipboard ──────────────────────────────────────────────── */
  const handleCopy = useCallback(async () => {
    if (selectedCells.size === 0) return;

    const rowIds = displayRecords.map(r => r.id || r.tool_id);
    const colKeys = visibleColumns.map(c => c.key);

    // Determine the bounding box of the selection
    const selRows = new Set();
    const selCols = new Set();
    for (const cellKey of selectedCells) {
      const [rowId, colKey] = cellKey.split(':');
      selRows.add(rowId);
      selCols.add(colKey);
    }

    // Ordered rows and columns
    const orderedRows = rowIds.filter(id => selRows.has(id));
    const orderedCols = colKeys.filter(k => selCols.has(k));

    // Build tab-separated text
    const lines = [];
    for (const rowId of orderedRows) {
      const record = displayRecords.find(r => (r.id || r.tool_id) === rowId);
      if (!record) continue;
      const vals = orderedCols.map(colKey => {
        const v = getEffectiveValue(record, colKey, pendingChanges);
        return v == null ? '' : String(v);
      });
      lines.push(vals.join('\t'));
    }

    const text = lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback: older browsers
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  }, [selectedCells, displayRecords, visibleColumns, pendingChanges]);

  /* ── Paste from clipboard ───────────────────────────────────────────── */
  const handlePaste = useCallback(async () => {
    if (!selection) return;

    let text;
    try {
      text = await navigator.clipboard.readText();
    } catch {
      return; // clipboard not available
    }
    if (!text) return;

    // Parse tab-separated clipboard
    const pasteRows = text.split(/\r?\n/).filter(line => line.length > 0);
    const pasteGrid = pasteRows.map(line => line.split('\t'));

    const rowIds = displayRecords.map(r => r.id || r.tool_id);
    const colKeys = visibleColumns.map(c => c.key);

    // Start from anchor position
    const startRowIdx = rowIds.indexOf(selection.anchor.rowId);
    const startColIdx = colKeys.indexOf(selection.anchor.colKey);
    if (startRowIdx === -1 || startColIdx === -1) return;

    // Strict columns refuse values not on their list (left unchanged, counted).
    let skipped = 0;
    const skippedLabels = new Set();

    for (let pr = 0; pr < pasteGrid.length; pr++) {
      const targetRowIdx = startRowIdx + pr;
      if (targetRowIdx >= displayRecords.length) break;
      const rowId = rowIds[targetRowIdx];

      for (let pc = 0; pc < pasteGrid[pr].length; pc++) {
        const targetColIdx = startColIdx + pc;
        if (targetColIdx >= colKeys.length) break;
        const colKey = colKeys[targetColIdx];
        const col = colByKey[colKey];
        if (!col || col.readOnly) continue;

        let val = pasteGrid[pr][pc];

        // Coerce value to match column type
        if (col.type === 'number') {
          val = val === '' ? '' : Number(val);
          if (typeof val === 'number' && isNaN(val)) val = '';
        } else if (col.type === 'boolean') {
          val = val === 'true' || val === '1' || val === '\u2713';
        }

        val = normalizeStrict(col, val); // strict columns only; others untouched
        if (strictRejects(col, val, refOpts)) {
          skipped += 1;
          skippedLabels.add(col.label);
          continue;
        }

        commitValueByIds(rowId, colKey, val);
      }
    }
    setStrictNotice(skipped > 0 ? { count: skipped, labels: [...skippedLabels] } : null);
  }, [selection, displayRecords, visibleColumns, commitValueByIds, colByKey, refOpts]);

  /* ── Delete selected cells ──────────────────────────────────────────── */
  const handleDeleteSelected = useCallback(() => {
    const { writes, skipped, labels } = planDelete(selectedCells, colByKey, refOpts);
    for (const [rowId, colKey, emptyVal] of writes) commitValueByIds(rowId, colKey, emptyVal);
    setStrictNotice(skipped > 0 ? { count: skipped, labels } : null);
  }, [selectedCells, commitValueByIds, colByKey, refOpts]);

  /* ── Click outside → deactivate ─────────────────────────────────────── */
  const tableRef = useRef(null);
  useEffect(() => {
    if (!activeCell && !selection) return;
    function handleOutsideClick(e) {
      // Only clear if click is outside the entire grid container (not just the table)
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setActiveCell(null);
        setEditingCell(null);
        setSelection(null);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [activeCell, selection]);

  /* ── Grid-level cell navigation (all cells, not just editable) ───────── */
  // Arrow key navigation always enters SELECT mode so the grid container
  // keeps focus and the keydown handler stays active between every cell.
  // Only click and Tab enter EDIT mode.
  const navigateToCell = useCallback((fromRowId, fromColKey, direction) => {
    const rowIds = displayRecords.map(r => r.id || r.tool_id);
    const colKeys = visibleColumns.map(c => c.key);
    if (rowIds.length === 0 || colKeys.length === 0) return;

    const curRow = rowIds.indexOf(fromRowId);
    const curCol = colKeys.indexOf(fromColKey);
    if (curRow === -1 || curCol === -1) return;

    let nextRow = curRow;
    let nextCol = curCol;
    let clamped = false;

    if (direction === 'next') {
      nextCol++;
      if (nextCol >= colKeys.length) { nextCol = 0; nextRow++; }
      if (nextRow >= rowIds.length) { nextRow = 0; }
    } else if (direction === 'prev') {
      nextCol--;
      if (nextCol < 0) { nextCol = colKeys.length - 1; nextRow--; }
      if (nextRow < 0) { nextRow = rowIds.length - 1; }
    } else if (direction === 'up') {
      if (curRow === 0) clamped = true;
      else nextRow = curRow - 1;
    } else if (direction === 'down') {
      if (curRow === rowIds.length - 1) clamped = true;
      else nextRow = curRow + 1;
    } else if (direction === 'left') {
      if (curCol === 0) clamped = true;
      else nextCol = curCol - 1;
    } else if (direction === 'right') {
      if (curCol === colKeys.length - 1) clamped = true;
      else nextCol = curCol + 1;
    }

    // At boundary: re-assert current cell so highlight isn't lost after editor commit
    if (clamped) {
      nextRow = curRow;
      nextCol = curCol;
    }

    const nextRowId = rowIds[nextRow];
    const nextColKey = colKeys[nextCol];
    const col = colByKey[nextColKey];

    // Always set activeCell for highlight
    setActiveCell({ rowId: nextRowId, colKey: nextColKey });
    setSelection(null);

    if (col && !col.readOnly) {
      // Editable cell: also open editor
      setEditingCell({ rowId: nextRowId, colKey: nextColKey });
    } else {
      // Read-only cell: no editor, keep focus on grid container
      setEditingCell(null);
      containerRef.current?.focus();
    }
    scrollToCell(nextRowId, nextColKey);
  }, [displayRecords, visibleColumns, colByKey, scrollToCell]);

  const navigateSelection = useCallback((direction) => {
    // Use selection anchor if available, otherwise fall back to activeCell
    const from = selection?.anchor || activeCell;
    if (!from) return;
    navigateToCell(from.rowId, from.colKey, direction);
  }, [selection, activeCell, navigateToCell]);

  /* ── Keyboard shortcuts (grid-level) ────────────────────────────────── */
  const containerRef = useRef(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    function handleKeyDown(e) {
      // Inside an active editor input: let the editor handle keys (via stopPropagation)
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA') return;

      // Tab / Shift+Tab: cycle through ALL cells (trap focus in grid)
      if (e.key === 'Tab') {
        e.preventDefault();
        if (selection?.anchor) {
          navigateSelection(e.shiftKey ? 'prev' : 'next');
        } else if (displayRecords.length > 0 && visibleColumns.length > 0) {
          // No selection yet: select first cell
          const firstRowId = displayRecords[0].id || displayRecords[0].tool_id;
          const firstColKey = visibleColumns[0].key;
          const col = colByKey[firstColKey];
          setActiveCell({ rowId: firstRowId, colKey: firstColKey });
          if (col && !col.readOnly) {
            setEditingCell({ rowId: firstRowId, colKey: firstColKey });
          } else {
            setEditingCell(null);
            setSelection({ anchor: { rowId: firstRowId, colKey: firstColKey }, focus: { rowId: firstRowId, colKey: firstColKey } });
          }
        }
        return;
      }

      // Arrow keys: navigate cells in SELECT mode
      if (e.key === 'ArrowUp')    { e.preventDefault(); navigateSelection('up'); return; }
      if (e.key === 'ArrowDown')  { e.preventDefault(); navigateSelection('down'); return; }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); navigateSelection('left'); return; }
      if (e.key === 'ArrowRight') { e.preventDefault(); navigateSelection('right'); return; }

      if (e.key === 'Escape') {
        setSelection(null);
        return;
      }

      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedCells.size > 0) {
        e.preventDefault();
        handleDeleteSelected();
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'd' || e.key === 'D') {
          e.preventDefault();
          handleFillDown();
          return;
        }
        if (e.key === 'c' || e.key === 'C') {
          e.preventDefault();
          handleCopy();
          return;
        }
        if (e.key === 'v' || e.key === 'V') {
          e.preventDefault();
          handlePaste();
          return;
        }
      }
    }

    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, [editingCell, activeCell, selection, selectedCells, displayRecords, visibleColumns, colByKey, navigateSelection, handleFillDown, handleCopy, handlePaste, handleDeleteSelected]);

  /* ── Save/Cancel handlers ───────────────────────────────────────────── */
  function handleSave() { if (onSave) onSave(pendingChanges); }
  function handleCancel() {
    setActiveCell(null);
    setEditingCell(null);
    setSelection(null);
    if (clearPendingChanges) clearPendingChanges();
    if (onCancel) onCancel();
  }

  /* ── Empty state ────────────────────────────────────────────────────── */
  if (!records || records.length === 0) {
    return <div className="gv-empty">No records in current filter.</div>;
  }

  const sortCount = (sortKeys || []).length;
  const selectionCount = selectedCells.size;

  return (
    <div className="gv-container" ref={containerRef} tabIndex={-1}>
      {/* ── Dirty state banner ──────────────────────────────────────────── */}
      {isDirty && (
        <div className="gv-dirty-banner">
          <span className="gv-dirty-banner-text">
            {saving
              ? 'Saving...'
              : `You have unsaved changes (${pendingChanges.size} record${pendingChanges.size !== 1 ? 's' : ''})`}
          </span>
          {saveError && <span className="gv-dirty-banner-error">{saveError}</span>}
          <button type="button" className="lrc-btn lrc-btn-primary gv-dirty-btn"
            onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button type="button" className="lrc-btn gv-dirty-btn" onClick={handleCancel} disabled={saving}>
            Cancel
          </button>
        </div>
      )}

      {saveError && !isDirty && (
        <div className="gv-dirty-banner">
          <span className="gv-dirty-banner-error">{saveError}</span>
        </div>
      )}

      {/* ── Grid toolbar ────────────────────────────────────────────────── */}
      <div className="gv-toolbar">
        <span className="gv-toolbar-count">
          {displayRecords.length} record{displayRecords.length !== 1 ? 's' : ''}
          {selectionCount > 1 && (
            <span className="gv-toolbar-sel-count"> &middot; {selectionCount} cells selected</span>
          )}
        </span>
        {strictNotice && (
          <span className="gv-toolbar-notice" role="status" data-testid="gv-strict-notice">
            {strictNotice.count} {strictNotice.labels.join(' / ')}{' '}
            cell{strictNotice.count !== 1 ? 's' : ''} left unchanged &mdash; only
            values on the list can be written there
            <button type="button" className="gv-toolbar-notice-dismiss"
              onMouseDown={e => e.preventDefault()} onClick={() => setStrictNotice(null)}
              aria-label="Dismiss">&times;</button>
          </span>
        )}
        {hasColumnRange && (
          <button type="button" className="lrc-btn gv-toolbar-btn gv-fill-btn"
            onMouseDown={e => e.preventDefault()} onClick={handleFillDown}>
            Fill Down
          </button>
        )}
        <button type="button" className="lrc-btn gv-toolbar-btn"
          onMouseDown={e => e.preventDefault()} onClick={() => setSortPanelOpen(prev => !prev)}>
          Sort
          {sortCount > 0 && <span className="gv-toolbar-badge">{sortCount}</span>}
        </button>
        <button type="button"
          className={`lrc-btn gv-toolbar-btn${activeViewId ? ' gv-toolbar-btn-active' : ''}`}
          onMouseDown={e => e.preventDefault()} onClick={() => { setViewsPanelOpen(true); setColPanelOpen(false); }}>
          Views{activeViewName ? ` \u00b7 ${activeViewName}` : ''}
        </button>
        <button type="button" className="lrc-btn gv-toolbar-btn"
          onMouseDown={e => e.preventDefault()} onClick={() => { setColPanelOpen(true); setViewsPanelOpen(false); }}>
          Columns
        </button>
      </div>

      {/* ── Sort panel ──────────────────────────────────────────────────── */}
      {sortPanelOpen && (
        <SortPanel columns={visibleColumns} sortKeys={sortKeys || []} onSortChange={setSortKeys} />
      )}

      {/* ── Scrollable table ────────────────────────────────────────────── */}
      <div className="gv-table-wrap" ref={tableRef}>
        <table className="gv-table" style={{ width: visibleColumns.reduce((sum, col) => sum + getColumnWidth(col), 0) }}>
          <colgroup>
            {visibleColumns.map(col => (
              <col key={col.key} style={{ width: getColumnWidth(col) }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {visibleColumns.map(col => {
                const sortInfo = sortKeyMap[col.key];
                return (
                  <th key={col.key}
                    className={`gv-th gv-th-sortable${col.key === 'supplier_tool_id' ? ' gv-sticky-col' : ''}${sortInfo ? ' gv-th-sorted' : ''}`}
                    onClick={() => handleHeaderClick(col.key)}>
                    <span className="gv-th-content">
                      {col.label}
                      {sortInfo && <SortArrow direction={sortInfo.direction} priority={sortInfo.priority} />}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {displayRecords.map((record, rowIdx) => {
              const rowId = record.id || record.tool_id;
              const isRowSelected = rowId === selectedRowId && !hasColumnRange;
              const rowChanges = pendingChanges.get(rowId);

              return (
                <tr key={rowId || rowIdx}
                  className={`gv-tr${isRowSelected ? ' gv-tr-selected' : ''}${rowIdx % 2 === 1 ? ' gv-tr-alt' : ''}`}>
                  {visibleColumns.map(col => {
                    const hasPending = rowChanges && col.key in rowChanges && rowChanges[col.key] !== undefined;
                    const isActive = activeCell?.rowId === rowId && activeCell?.colKey === col.key;
                    const isEditing = editingCell?.rowId === rowId && editingCell?.colKey === col.key;
                    const isEditable = !col.readOnly;
                    const cellKey = `${rowId}:${col.key}`;
                    const isCellSelected = selectedCells.has(cellKey);
                    const isAnchor = selection?.anchor?.rowId === rowId && selection?.anchor?.colKey === col.key;

                    const displayValue = hasPending
                      ? (col.type === 'boolean' ? (rowChanges[col.key] ? '\u2713' : '')
                        : col.type === 'number' ? fmtNumber(rowChanges[col.key])
                        : col.type === 'date' ? fmtDate(rowChanges[col.key])
                        : String(rowChanges[col.key] ?? ''))
                      : renderCellValue(record, col);

                    const rawForEdit = getRawValue(record, col, pendingChanges);

                    function handleCellClick(e) {
                      e.stopPropagation();
                      setSelectedRowId(rowId);

                      // Shift+click: extend selection vertically within anchor column
                      if (e.shiftKey && selection?.anchor) {
                        setSelection(prev => ({ ...prev, focus: { rowId, colKey: prev.anchor.colKey } }));
                        setEditingCell(null);
                        return;
                      }

                      // Single click: always set anchor + highlight
                      const anchor = { rowId, colKey: col.key };
                      setSelection({ anchor, focus: anchor });
                      setActiveCell({ rowId, colKey: col.key });

                      if (isEditable) {
                        setEditingCell({ rowId, colKey: col.key });
                      } else {
                        setEditingCell(null);
                      }
                    }

                    function handleCellDoubleClick(e) {
                      e.stopPropagation();
                      if (!isEditable) return;
                      setActiveCell({ rowId, colKey: col.key });
                      setEditingCell({ rowId, colKey: col.key });
                      setSelection(null);
                    }

                    function handleCommit(newVal) {
                      commitCellValue(record, col, newVal);
                      setEditingCell(null);
                    }

                    function handleCancelEdit() {
                      setEditingCell(null);
                    }

                    function handleNav(dir) {
                      if (dir === 'up' || dir === 'down' || dir === 'left' || dir === 'right') {
                        navigateToCell(rowId, col.key, dir);
                      } else {
                        navigate(dir, rowId, col.key);
                      }
                    }

                    return (
                      <td key={col.key}
                        data-cell={cellKey}
                        className={
                          'gv-td' +
                          (col.key === 'supplier_tool_id' ? ' gv-sticky-col' : '') +
                          (hasPending ? ' gv-td-dirty' : '') +
                          (col.readOnly ? ' gv-td-readonly' : ' gv-td-editable') +
                          (isActive ? ' gv-td-active' : '') +
                          (isCellSelected && !isActive ? ' gv-td-sel' : '') +
                          (isAnchor && !isActive ? ' gv-td-anchor' : '')
                        }
                        onClick={handleCellClick}
                        onDoubleClick={handleCellDoubleClick}>
                        {isEditing && isEditable ? (
                          col.type === 'boolean' ? (
                            <BooleanCellEditor value={rawForEdit}
                              onCommit={(v) => { commitCellValue(record, col, v); setEditingCell(null); }}
                              onNav={handleNav} />
                          ) : col.type === 'combobox' ? (
                            <ComboBoxCellEditor value={rawForEdit} options={refOpts[col.refKey] || []}
                              strict={!!col.strict}
                              onCommit={handleCommit} onCancel={handleCancelEdit} onNav={handleNav} />
                          ) : col.type === 'date' ? (
                            <DateCellEditor value={rawForEdit}
                              onCommit={handleCommit} onCancel={handleCancelEdit} onNav={handleNav} />
                          ) : col.type === 'number' ? (
                            <NumberCellEditor value={rawForEdit}
                              onCommit={handleCommit} onCancel={handleCancelEdit} onNav={handleNav} />
                          ) : (
                            <TextCellEditor value={rawForEdit}
                              onCommit={handleCommit} onCancel={handleCancelEdit} onNav={handleNav} />
                          )
                        ) : (
                          col.type === 'boolean' ? (
                            <span className={`gv-bool${record[col.key] || (hasPending && rowChanges[col.key]) ? ' gv-bool-true' : ''}`}>
                              {displayValue}
                            </span>
                          ) : displayValue
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Column Visibility Panel ─────────────────────────────────────── */}
      <ColumnVisibilityPanel open={colPanelOpen} onClose={() => setColPanelOpen(false)}
        columnVisibility={columnVisibility} onToggle={toggleColumn}
        onShowAll={showAllColumns} onHideAll={hideAllColumns}
        columns={columns} />

      {/* ── Saved Views Panel ───────────────────────────────────────────── */}
      {viewsPanelOpen && (
        <SavedViewsPanel
          screen={screen}
          callerEmail={callerEmail}
          currentColumnVisibility={columnVisibility}
          currentSortKeys={sortKeys}
          savedViews={savedViews || []}
          onSaveView={saveView}
          onLoadView={loadView}
          onDeleteView={deleteView}
          onClose={() => setViewsPanelOpen(false)}
          columns={columns}
        />
      )}
    </div>
  );
}
